import React from 'react';
import { Definitions } from './lexer-parser';
import { BlockQuote } from './parsers/block-quote/block-quote';
import { BoldItalic } from './parsers/bold-italic/bold-italic';
import { Headers } from './parsers/headers/headers';
import { IfStatement } from './parsers/if-statement/if-statement';
import { InlineImage } from './parsers/inline-image/inline-image';
import { Mustache } from './parsers/mustache/mustache';
import { List } from './parsers/lists/lists';
import { UrlLink } from './parsers/url-link/url-link';
import { Table } from './parsers/table/table';
import {
  CustomComponent,
  CustomComponentType,
} from './parsers/custom-components/custom-components';
import { AddCssClassToBody } from './utils/add-css-class-body/add-css-class-body';

let classAdded = false;
export const pipeWithLexer = (...fns) => {
  return (x) => {
    const { def } = x;
    return fns.reduce((v, f) => {
      const { input } = v;
      return f({ ...v, lex: def.lexer(input) });
    }, x);
  };
};

const merge = (a: any[], b: any[], prop: string) => {
  const reduced = a.filter(function (aitem) {
    return !b.find(function (bitem) {
      return aitem[prop] === bitem[prop];
    });
  });

  return reduced.concat(b);
};

const addNewLineToEndOfString = (input: string): string => input + '\n\n';

const removeUnwantedHtml = (
  input: string,
  customComponents?: CustomComponentType[]
) => {
  let newInput = input;
  // With new toastui editor in strapi, custom components will need
  // to be surrounded by a code block (this is to support the strapi editor)
  // we need to remove that as it is not needed in our parser
  // the code block should look like:
  // <code data-backticks="1">&lt;UsStatesSelect /&gt;</code>

  // Create regex for each customcomponent that we have
  // that way we don't accidently grab a real code block
  customComponents?.forEach(({ name }) => {
    const codeComponentRegex = `(<code([^>]+)>)&lt;(?<value>${name}.*?) \/&gt;(<([^>]+)code>)`;

    input?.match(new RegExp(codeComponentRegex, 'gi'))?.forEach((m) => {
      const matched = m.match(new RegExp(codeComponentRegex, 'i'));
      if (matched && matched?.length > 0 && matched?.groups?.value) {
        newInput = newInput.replace(m, `<${matched?.groups?.value} />`);
      }
    });
  });

  return newInput;
};

export type ComponentType = HTMLElement | JSX.Element | React.FC;
export type UrlComponentType = ({
  url,
  title,
}: {
  url: string;
  title: string;
}) => ComponentType;
export type ParserOptions = {
  customComponents?: CustomComponentType[];
  urlComponents?: {
    internal?: UrlComponentType;
    external?: UrlComponentType;
  };
  customComponentWrapper?: ComponentType;
  env?: string;
};

export class Parser {
  options?: ParserOptions;
  lexer;
  ref;

  constructor(options?: ParserOptions) {
    this.ref = React.createRef();
    this.options = options || {};
    this.lexer = Definitions();

    // add css to head
    if (!classAdded) {
      classAdded = AddCssClassToBody(`
      /* table */
      #cvna-parser table {
        border: 1px solid rgba(0, 0, 0, 0.1);
        margin: 12px 0 14px;
        color: #222;
        width: auto;
        border-collapse: collapse;
        box-sizing: border-box;
      }

      #cvna-parser table th {
        font-weight: 300;
        padding-top: 6px;
      }

      #cvna-parser td,
      #cvna-parser th {
        border: 1px solid #dddddd;
        text-align: left;
        padding: 5px 14px 5px 12px;
        height: 32px;
      }

      #cvna-parser td[align="right"], 
      #cvna-parser th[align="right"] {
        text-align: right;
      }

      #cvna-parser td[align="left"],
      #cvna-parser th[align="left"] {
        text-align: left;
      }
      
      #cvna-parser td[align="center"],
      #cvna-parser th[align="center"] {
        text-align: center;
      }

      /* list */
      #cvna-parser li {
        margin-left: 0px;
        margin-bottom: 0px;
      }

      #cvna-parser li::before {
        margin-left: -20px;
        margin-right: 10px;
      }

      #cvna-parser ul p, 
      #cvna-parser ol p {
        margin: 0;
      }

      /* link */
      #cvna-parser a {
        text-decoration: underline;
        color: var(--cvna-color-blue-2, #00B3D9);
      }

      #cvna-parser a:hover {
        color: var(--cvna-color-blue-2, #00B3D9);
      }

      /* blockquote */
      #cvna-parser blockquote {
        background:#f9f9f9;
        border-left: 10px solid #ccc;
        margin: 1.5em 10px;
        padding: 1em 10px 1em 10px;
      }

      #cvna-parser .ProseMirror-trailingBreak {
        display: none;
      }

      /* inline code */
      #cvna-parser code {
        padding: 0.2rem 0.5rem;
        margin: 0 0.2rem;
        font-size: 90%;
        background: #F1F1F1;
        border: 1px solid #E1E1E1;
        border-radius: 4px;
      }

      /* code block */
      #cvna-parser pre > code {
        display: block;
        background: #F1F1F1;
        border: 1px solid #E1E1E1;
        border-radius: 4px;
        font-size: 90%;
        padding: 0.2rem 0.5rem;
      }

      /* image */
      #cvna-parser img {
        max-width:93%; 
        width:auto;
      }
      `);
    }
  }

  addCustomComponents(cc: CustomComponentType[]) {
    this.options = {
      ...this.options,
      customComponents: merge(
        this.options?.customComponents as any[],
        cc,
        'name'
      ),
    };
  }

  addUrlComponents(uc: {
    internal?: UrlComponentType;
    external?: UrlComponentType;
  }) {
    this.options = {
      ...this.options,
      urlComponents: {
        ...this.options?.urlComponents,
        ...uc,
      },
    };
  }

  render(input: string, data?: {}, cb?: unknown) {
    if (!input) return null;

    // add \n to end of article if one does not exist
    let inputString = addNewLineToEndOfString(input);
    // do some cleanup of unwanted html code from editor
    inputString = removeUnwantedHtml(
      inputString,
      this.options?.customComponents
    );

    let result = { input: inputString };
    try {
      result = pipeWithLexer(
        IfStatement(),
        Mustache(),
        Table(),
        List(),
        BoldItalic(),
        Headers(),
        InlineImage(),
        BlockQuote(),
        UrlLink({
          customComponentWrapper: this.options?.customComponentWrapper,
          urlComponents: this.options?.urlComponents,
        }),
        CustomComponent({
          customComponentWrapper: this.options?.customComponentWrapper,
          customComponents: this.options?.customComponents,
          cb,
          env: this.options?.env,
        })
      )({ input: inputString, data, def: this.lexer });
    } catch (error) {
      console.error(error);
    }

    return (
      <div
        ref={this.ref.current}
        id="cvna-parser"
        dangerouslySetInnerHTML={{ __html: result.input }}
        style={{ whiteSpace: 'pre-line' }}
      ></div>
    );
  }
}
